import apiService, { ApiError } from './apiService';

/**
 * Shopper Service
 * Handles admin shopper list/detail API operations.
 */
class ShopperService {
  formatDateForAPI(date, isEndDate = false) {
    if (!date) return null;

    const dateObj = new Date(date);
    if (isNaN(dateObj.getTime())) return null;

    const year = dateObj.getFullYear();
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const day = String(dateObj.getDate()).padStart(2, '0');
    const time = isEndDate ? '23:59:59' : '00:00:00';

    return `${year}-${month}-${day} ${time}`;
  }

  isZeroDate(value) {
    return !value || String(value).startsWith('0001-01-01');
  }

  formatDisplayDate(value) {
    if (this.isZeroDate(value)) return '-';

    const date = new Date(value);
    if (isNaN(date.getTime())) return '-';

    return date.toLocaleDateString('en-US');
  }

  formatCurrencyEUR(value) {
    if (value === null || value === undefined || value === '') return '-';

    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) return '-';

    return new Intl.NumberFormat('en-IE', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(numericValue);
  }

  splitName(name) {
    const safeName = String(name || '').trim();
    if (!safeName) return { firstName: 'N/A', lastName: 'N/A' };

    const parts = safeName.split(/\s+/);
    return {
      firstName: parts[0] || 'N/A',
      lastName: parts.slice(1).join(' ') || 'N/A',
    };
  }

  normalizeStatus(statusCode) {
    const labels = {
      A: 'Active',
      D: 'Deleted',
      S: 'Suspended',
      N: 'Inactive',
    };

    return (statusCode && labels[statusCode]) || statusCode || '-';
  }

  async getShopperList(params = {}, options = {}) {
    const { page = 1, size = 20, keyword, dateFrom, dateTo, order, status } = params;

    try {
      const requestData = {
        page,
        size,
      };

      if (status) {
        requestData.status = status;
      }

      if (order) {
        requestData.order = order;
      }

      if (keyword && keyword.trim()) {
        requestData.keyword = keyword.trim();
      }

      if (dateFrom && dateTo) {
        const startDate = this.formatDateForAPI(dateFrom, false);
        const endDate = this.formatDateForAPI(dateTo, true);

        if (startDate && endDate) {
          requestData.dateFilter = {
            start: startDate,
            end: endDate,
          };
        }
      }

      const response = await apiService.skaiOneRequest('/api/v1/shopper/list', {
        method: 'POST',
        data: requestData,
        ...options,
      });

      if (!response || !response.data) {
        throw new ApiError(
          'Invalid response format from shopper list API',
          200,
          'INVALID_RESPONSE'
        );
      }

      return response;
    } catch (error) {
      console.error('Failed to fetch shopper list:', error);

      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError('Failed to retrieve shopper list', 0, 'SHOPPER_LIST_ERROR', {
        originalError: error.message,
      });
    }
  }

  async getShopperDetails(shopperId, options = {}) {
    if (!shopperId) {
      throw new ApiError('Shopper ID is required', 400, 'MISSING_SHOPPER_ID');
    }

    try {
      const response = await apiService.skaiOneRequest(`/api/v1/shopper/admin/${shopperId}`, {
        method: 'GET',
        ...options,
      });

      return response;
    } catch (error) {
      console.error(`Failed to fetch shopper details for ID ${shopperId}:`, error);

      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Failed to retrieve shopper details',
        0,
        'SHOPPER_DETAILS_ERROR',
        { shopperId, originalError: error.message }
      );
    }
  }

  async exportShopperList(params = {}, options = {}) {
    const { keyword, dateFrom, dateTo, order, referrerAccountID } = params;

    try {
      const requestData = {};

      if (order) {
        requestData.order = order;
      }

      if (keyword && keyword.trim()) {
        requestData.keyword = keyword.trim();
      }

      if (referrerAccountID) {
        requestData.referrerAccountID = referrerAccountID;
      }

      if (dateFrom && dateTo) {
        const startDate = this.formatDateForAPI(dateFrom, false);
        const endDate = this.formatDateForAPI(dateTo, true);

        if (startDate && endDate) {
          requestData.dateFilter = {
            start: startDate,
            end: endDate,
          };
        }
      }

      const response = await apiService.skaiOneRequest(
        '/api/v1/shopper/list-export',
        {
          method: 'POST',
          data: requestData,
          ...options,
        }
      );

      if (!response?.data || typeof response.data !== 'object') {
        throw new ApiError(
          'Invalid response format from shopper export API. Expected JSON object.',
          200,
          'INVALID_RESPONSE'
        );
      }

      return response;
    } catch (error) {
      console.error('Failed to export shopper list:', error);

      if (error instanceof ApiError) {
        throw error;
      }

      throw new ApiError(
        'Failed to export shopper list',
        0,
        'SHOPPER_EXPORT_ERROR',
        { originalError: error.message }
      );
    }
  }

  transformShopperData(apiShopper) {
    if (!apiShopper) return null;

    const { firstName, lastName } = this.splitName(apiShopper.name);
    const fullName = apiShopper.name || [firstName, lastName].join(' ').trim();
    const accountStatusLabel = this.normalizeStatus(apiShopper.status);
    const accountTypeLabel = apiShopper.plus ? 'Plus' : 'Basic';
    const marketerBalance = apiShopper.marketerBalance || {};
    const quota = apiShopper.quota || apiShopper.quotas || {};
    const linkedBanks = Array.isArray(apiShopper.linkedBanks)
      ? apiShopper.linkedBanks.map((bank, index) => ({
          ...bank,
          title:
            bank.institutionName ||
            bank.institutionId ||
            bank.title ||
            `Linked Bank ${index + 1}`,
          institutionId: bank.institutionId || '',
          institutionName: bank.institutionName || '',
          accountId: bank.accountId || bank.accountID || '',
          // BE already prefixes `**` for masked sessions; for revealed sessions
          // it returns the raw 4-digit Plaid mask. Use as-is to avoid double `**`.
          cardNumber: bank.cardNumber || bank.mask || '',
          name: bank.name || '',
          officialName: bank.officialName || '',
          status: bank.status || '',
          note:
            bank.note ||
            (bank.status === 'ITEM_LOGIN_REQUIRED'
              ? '(Item login required)'
              : ''),
        }))
      : [];

    return {
      id: apiShopper.id,
      accountId: apiShopper.accountId,
      revealed: apiShopper.revealed === true,
      firstName,
      lastName,
      fullName: fullName || 'N/A',
      email: apiShopper.email || 'N/A',
      phone: apiShopper.phone || 'N/A',
      plus: apiShopper.plus ?? false,
      status: accountTypeLabel,
      accountStatusCode: apiShopper.status,
      accountStatus: accountStatusLabel,
      accountStatusLabel,
      dateOfBirth: apiShopper.dateOfBirth,
      birthdate: this.formatDisplayDate(apiShopper.dateOfBirth),
      joinDate: this.formatDisplayDate(apiShopper.createdAt),
      subscriptionDate: this.formatDisplayDate(apiShopper.paidStart),
      subscriptionDueDate: this.formatDisplayDate(apiShopper.paidExpired),
      paidStart: apiShopper.paidStart,
      paidExpired: apiShopper.paidExpired,
      referralCode: apiShopper.referral || 'N/A',
      referral: apiShopper.referral || 'N/A',
      careerPoint: apiShopper.careerPoint ?? marketerBalance.balance ?? 0,
      generatedCareerPoint: apiShopper.generatedCareerPoint ?? null,
      loyaltyPoint: apiShopper.loyaltyPoint ?? 0,
      contributedVp: apiShopper.contributedVp ?? 0,
      balance: apiShopper.balance ?? 0,
      lifetimeCashback: apiShopper.lifetimeCashback ?? null,
      lifetimeWithdraw: apiShopper.lifetimeWithdraw ?? null,
      cashbackEE: this.formatCurrencyEUR(apiShopper.cashbackEE),
      cashbackEne: this.formatCurrencyEUR(apiShopper.cashbackEE),
      cashbackMerchant: this.formatCurrencyEUR(apiShopper.cashbackMerchant),
      withdrawalBalance: this.formatCurrencyEUR(apiShopper.withdrawalBalance),
      availableCashback: this.formatCurrencyEUR(apiShopper.balance),
      iban: apiShopper.iban || '',
      bankName: apiShopper.bankName || '',
      bankAccountNumber: apiShopper.bankAccountNumber || '',
      beneficiaryName: apiShopper.beneficiaryName || '',
      codiceFiscale: apiShopper.codiceFiscale || apiShopper.taxCode || '',
      codiceFiscaleAddress:
        apiShopper.codiceFiscaleAddress ||
        apiShopper.billingAddress ||
        apiShopper.address ||
        '',
      codiceFiscaleProvince:
        apiShopper.codiceFiscaleProvince ||
        apiShopper.billingProvince ||
        apiShopper.province ||
        '',
      codiceFiscaleCity:
        apiShopper.codiceFiscaleCity || apiShopper.billingCity || apiShopper.city || '',
      codiceFiscaleZipCode:
        apiShopper.codiceFiscaleZipCode ||
        apiShopper.billingZipCode ||
        apiShopper.zip ||
        '',
      codiceFiscaleChecked: apiShopper.codiceFiscaleChecked === true,
      codiceFiscaleCheckRawResponse:
        apiShopper.codiceFiscaleCheckRawResponse || {},
      vat: apiShopper.vat || '',
      taxScheme: apiShopper.taxScheme || '',
      upline: apiShopper.upline || '',
      recommender: apiShopper.upline || apiShopper.recommenderName || '-',
      directPartner: apiShopper.partner ?? 0,
      partner: apiShopper.partner ?? 0,
      totalDownline: apiShopper.downline ?? 0,
      downline: apiShopper.downline ?? 0,
      quota,
      payAndGoActionRequired: apiShopper.payAndGoActionRequired ?? null,
      linkedBanks,
      image: apiShopper.image,
      cover: apiShopper.cover,
      emailVerified: apiShopper.emailVerified,
      phoneVerified: apiShopper.phoneVerified,
      deleted: apiShopper.deleted || null,
      deletionReason: apiShopper.deletionReason || '',
      unblockedAt: apiShopper.unblockedAt || null,
      unblockedBy: apiShopper.unblockedBy || '',
      unblockedByName: apiShopper.unblockedByName || '',
      unblockReason: apiShopper.unblockReason || '',
      modifiedAt: apiShopper.modifiedAt,
      modifiedBy: apiShopper.modifiedBy,
      modifiedByName: apiShopper.modifiedByName,
      lastModified: this.formatDisplayDate(apiShopper.modifiedAt),
      createdAt: apiShopper.createdAt,
      raw: apiShopper,
    };
  }

  async getFormattedShopperList(params = {}, options = {}) {
    try {
      const response = await this.getShopperList(params, options);
      const rows = Array.isArray(response.data.data) ? response.data.data : [];
      const transformedData = {
        ...response.data,
        data: rows.map(shopper => this.transformShopperData(shopper)),
      };

      return { ...response, data: transformedData };
    } catch (error) {
      console.error('Failed to get formatted shopper list:', error);
      throw error;
    }
  }

  async getFormattedShopperDetails(shopperId, options = {}) {
    try {
      const response = await this.getShopperDetails(shopperId, options);

      return {
        ...response,
        data: this.transformShopperData(response.data),
      };
    } catch (error) {
      console.error(
        `Failed to get formatted shopper details for ID ${shopperId}:`,
        error
      );
      throw error;
    }
  }

  /**
   * Update shopper personal info (admin). All fields are optional — only the
   * fields you send get updated (partial update). Caller should diff against
   * the original to skip unchanged fields.
   * Endpoint: PUT /api/v1/shopper/admin/:id/personal-info
   *
   * `skipVerificationReset` (optional bool) preserves `phone_verified` /
   * `email_verified` flags when phone/email change; default behavior on BE
   * is to reset the flag on change.
   */
  async updatePersonalInfo(shopperId, payload, options = {}) {
    if (!shopperId) {
      throw new ApiError('Shopper ID is required', 400, 'MISSING_SHOPPER_ID');
    }
    if (!payload || typeof payload !== 'object') {
      throw new ApiError(
        'Payload is required',
        400,
        'FeatureInvalidPayload'
      );
    }

    try {
      const response = await apiService.skaiOneRequest(
        `/api/v1/shopper/admin/${shopperId}/personal-info`,
        {
          method: 'PUT',
          data: payload,
          ...options,
        }
      );
      return response;
    } catch (error) {
      console.error(
        `Failed to update personal info for shopper ${shopperId}:`,
        error
      );
      if (error instanceof ApiError) throw error;
      throw new ApiError(
        'Failed to update personal info',
        0,
        'PERSONAL_INFO_UPDATE_ERROR',
        { shopperId, originalError: error.message }
      );
    }
  }

  /**
   * Update shopper codice fiscale (admin). Triggers Stripe customer sync
   * (always — per release notes 5.1) + bank row update (type=S) + shopper
   * table update.
   * Endpoint: PUT /api/v1/shopper/admin/:id/codice-fiscale
   *
   * The form uses `billingTown` internally (so the reused Partita Iva
   * validate response can autofill without per-field mapping). We map it
   * to `billingCity` for the PUT body.
   */
  async updateCodiceFiscale(shopperId, payload, options = {}) {
    if (!shopperId) {
      throw new ApiError('Shopper ID is required', 400, 'MISSING_SHOPPER_ID');
    }
    const trim = v => String(v ?? '').trim();
    const body = {
      codiceFiscale: trim(payload?.codiceFiscale),
      billingAddress: trim(payload?.billingAddress),
      billingProvince: trim(payload?.billingProvince),
      billingCity: trim(payload?.billingCity ?? payload?.billingTown),
      billingZipCode: trim(payload?.billingZipCode),
      codiceFiscaleChecked: payload?.codiceFiscaleChecked === true,
      codiceFiscaleCheckRawResponse:
        payload?.codiceFiscaleCheckRawResponse ?? {},
    };

    const required = [
      'codiceFiscale',
      'billingAddress',
      'billingProvince',
      'billingCity',
      'billingZipCode',
    ];
    for (const key of required) {
      if (!body[key]) {
        throw new ApiError(
          `${key} is required`,
          400,
          'FeatureInvalidPayload'
        );
      }
    }

    try {
      const response = await apiService.skaiOneRequest(
        `/api/v1/shopper/admin/${shopperId}/codice-fiscale`,
        {
          method: 'PUT',
          data: body,
          ...options,
        }
      );
      return response;
    } catch (error) {
      console.error(
        `Failed to update Codice Fiscale for shopper ${shopperId}:`,
        error
      );
      if (error instanceof ApiError) throw error;
      throw new ApiError(
        'Failed to update Codice Fiscale',
        0,
        'CODICE_FISCALE_UPDATE_ERROR',
        { shopperId, originalError: error.message }
      );
    }
  }

  /**
   * Update shopper bank (admin) — IBAN + beneficiary name only.
   * BE upserts the type=S bank row; address/tax columns untouched.
   * No Stripe sync triggered.
   */
  async updateShopperBank(shopperId, payload, options = {}) {
    if (!shopperId) {
      throw new ApiError('Shopper ID is required', 400, 'MISSING_SHOPPER_ID');
    }

    const iban = String(payload?.iban ?? '').trim();
    const beneficiaryName = String(payload?.beneficiaryName ?? '').trim();
    if (!iban || !beneficiaryName) {
      throw new ApiError(
        'IBAN and Beneficiary Name are required',
        400,
        'FeatureInvalidPayload'
      );
    }

    try {
      const response = await apiService.skaiOneRequest(
        `/api/v1/shopper/admin/${shopperId}/bank`,
        {
          method: 'PUT',
          data: { iban, beneficiaryName },
          ...options,
        }
      );
      return response;
    } catch (error) {
      console.error(`Failed to update shopper bank ${shopperId}:`, error);
      if (error instanceof ApiError) throw error;
      throw new ApiError(
        'Failed to update shopper bank',
        0,
        'SHOPPER_BANK_UPDATE_ERROR',
        { shopperId, originalError: error.message }
      );
    }
  }

  /**
   * Immediately delete shopper account (admin). Requires admin reason and password.
   * Endpoint: POST /api/v1/shopper/admin/:id/delete
   */
  async deleteShopperAdmin(shopperId, { reason, password }, options = {}) {
    if (!shopperId) {
      throw new ApiError('Shopper ID is required', 400, 'MISSING_SHOPPER_ID');
    }
    const trimReason = String(reason || '').trim();
    const trimPassword = String(password || '').trim();
    if (!trimReason || !trimPassword) {
      throw new ApiError(
        'Reason and password are required',
        400,
        'FeatureInvalidPayload'
      );
    }
    try {
      const response = await apiService.skaiOneRequest(
        `/api/v1/shopper/admin/${shopperId}/delete`,
        {
          method: 'POST',
          data: { reason: trimReason, password: trimPassword },
          ...options,
        }
      );
      return response;
    } catch (error) {
      console.error(`Failed to delete shopper ${shopperId}:`, error);
      if (error instanceof ApiError) throw error;
      throw new ApiError(
        'Failed to delete shopper account',
        0,
        'SHOPPER_DELETE_ERROR',
        { shopperId, originalError: error.message }
      );
    }
  }

  /**
   * Unblock a deleted shopper account (admin) to lift 6-month credential re-registration block.
   * Requires admin reason and password.
   * Endpoint: POST /api/v1/shopper/admin/:id/unblock
   */
  async unblockShopperAdmin(shopperId, { reason, password }, options = {}) {
    if (!shopperId) {
      throw new ApiError('Shopper ID is required', 400, 'MISSING_SHOPPER_ID');
    }
    const trimReason = String(reason || '').trim();
    const trimPassword = String(password || '').trim();
    if (!trimReason || !trimPassword) {
      throw new ApiError(
        'Reason and password are required',
        400,
        'FeatureInvalidPayload'
      );
    }
    try {
      const response = await apiService.skaiOneRequest(
        `/api/v1/shopper/admin/${shopperId}/unblock`,
        {
          method: 'POST',
          data: { reason: trimReason, password: trimPassword },
          ...options,
        }
      );
      return response;
    } catch (error) {
      console.error(`Failed to unblock shopper ${shopperId}:`, error);
      if (error instanceof ApiError) throw error;
      throw new ApiError(
        'Failed to unblock shopper account',
        0,
        'SHOPPER_UNBLOCK_ERROR',
        { shopperId, originalError: error.message }
      );
    }
  }
}

const shopperService = new ShopperService();

export default shopperService;
export { ShopperService };
